/** * Analytics Service - Sammelt Benutzerinformationen für Tracking-Zwecke */ 

/** * Erfasst Benutzer- und Geräteinformationen * @returns {Object} - Gesammelte analytische Daten */
export const collectUserAnalytics = () => {
  try {
    const analytics = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent || 'Unbekannt',
      language: navigator.language || 'Unbekannt',
      platform: navigator.platform || 'Unbekannt',
      screenResolution: {
        width: window.screen.width,
        height: window.screen.height
      },
      windowSize: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio || 1,
      touchPoints: navigator.maxTouchPoints || 0,
      referrer: document.referrer || 'Direkt',
      deviceMemory: navigator.deviceMemory ? `${navigator.deviceMemory} GB` : 'Nicht verfügbar',
      connectionType: getConnectionType(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unbekannt',
      ipAddress: 'Wird vom Server ergänzt', // Will be populated by external service
      deviceType: getDeviceType(),
      browser: getBrowserInfo(),
      os: getOperatingSystem()
    };
    return analytics;
  } catch (error) {
    console.error('Fehler beim Sammeln von Analytics-Daten:', error);
    return {
      error: 'Fehler beim Sammeln von Analytics-Daten',
      errorMessage: error.message
    };
  }
};

/** * Sendet eine Anfrage an einen externen IP-Erfassungsservice
 * @param {string} orderId - Die ID der Bestellung
 * @returns {Promise<Object>} - Ergebnis mit der IP-Adresse */
export const captureIPAddress = async (orderId) => {
  try {
    // First try with ipify.org - a reliable IP service
    const response = await fetch('https://api.ipify.org?format=json');
    
    if (response.ok) {
      const ipData = await response.json();
      const ipAddress = ipData.ip;
      
      console.log('✅ IP-Adresse erfolgreich ermittelt:', ipAddress);
      
      // Store in database
      const { supabase } = await import('../lib/supabase');
      
      // Store in simple table
      const { data, error } = await supabase
        .from('ip_analytics_simple')
        .insert([{
          order_id: orderId,
          ip_address: ipAddress,
          user_agent: navigator.userAgent
        }])
        .select();
        
      if (error) {
        console.error('Fehler beim Speichern der IP:', error);
      }
      
      // Update order analytics data
      const { error: updateError } = await supabase
        .from('burger_orders_hb47x29k1a')
        .update({
          analytics_data: JSON.stringify({
            ...JSON.parse(JSON.stringify(await collectUserAnalytics())),
            ipAddress: ipAddress
          })
        })
        .eq('id', orderId);
        
      if (updateError) {
        console.error('Fehler beim Aktualisieren der Bestellung:', updateError);
      }
      
      return { success: true, ip: ipAddress };
    }
    
    // If ipify fails, try with ipapi.co as backup
    const backupResponse = await fetch('https://ipapi.co/json/');
    if (backupResponse.ok) {
      const backupData = await backupResponse.json();
      const backupIp = backupData.ip;
      
      console.log('✅ Backup IP-Adresse ermittelt:', backupIp);
      
      // Store in database
      const { supabase } = await import('../lib/supabase');
      
      // Store in simple table
      await supabase
        .from('ip_analytics_simple')
        .insert([{
          order_id: orderId,
          ip_address: backupIp,
          user_agent: navigator.userAgent
        }]);
        
      // Update order analytics data
      await supabase
        .from('burger_orders_hb47x29k1a')
        .update({
          analytics_data: JSON.stringify({
            ...JSON.parse(JSON.stringify(await collectUserAnalytics())),
            ipAddress: backupIp
          })
        })
        .eq('id', orderId);
      
      return { success: true, ip: backupIp };
    }
    
    throw new Error('Alle IP-Dienste fehlgeschlagen');
  } catch (error) {
    console.error('Fehler bei der IP-Adresserfassung:', error);
    return { error: error.message };
  }
};

/** * Ermittelt den Verbindungstyp * @returns {string} - Verbindungstyp */ 
const getConnectionType = () => {
  if (!navigator.connection) return 'Nicht verfügbar';
  const connection = navigator.connection;
  return connection.effectiveType || connection.type || 'Verbunden';
};

/** * Ermittelt den Gerätetyp basierend auf User-Agent und Bildschirmgröße * @returns {string} - Gerätetyp (Desktop, Tablet, Mobilgerät) */ 
const getDeviceType = () => {
  const ua = navigator.userAgent;
  
  // Prüfen auf Mobilgerät
  if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(ua) || 
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(ua.substr(0, 4))) {
    return 'Mobilgerät';
  }
  
  // Prüfen auf Tablet (basierend auf Bildschirmgröße)
  if (window.innerWidth <= 1024 && window.innerWidth > 640) {
    return 'Tablet';
  }
  
  // Ansonsten Desktop
  return 'Desktop';
};

/** * Ermittelt Browser-Informationen * @returns {Object} - Browser-Name und Version */ 
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = 'Unbekannt';
  let version = 'Unbekannt';

  // Chrome
  if (/Chrome/.test(ua) && !/Chromium|Edge|Edg|OPR|Opera/.test(ua)) {
    browser = 'Chrome';
    const match = ua.match(/Chrome\/(\d+\.\d+)/);
    version = match ? match[1] : 'Unbekannt';
  }
  // Firefox
  else if (/Firefox/.test(ua)) {
    browser = 'Firefox';
    const match = ua.match(/Firefox\/(\d+\.\d+)/);
    version = match ? match[1] : 'Unbekannt';
  }
  // Safari (ohne Chrome)
  else if (/Safari/.test(ua) && !/Chrome/.test(ua)) {
    browser = 'Safari';
    const match = ua.match(/Version\/(\d+\.\d+)/);
    version = match ? match[1] : 'Unbekannt';
  }
  // Edge (neuer auf Chromium basierend)
  else if (/Edg/.test(ua)) {
    browser = 'Edge';
    const match = ua.match(/Edg\/(\d+\.\d+)/);
    version = match ? match[1] : 'Unbekannt';
  }
  // Opera
  else if (/OPR|Opera/.test(ua)) {
    browser = 'Opera';
    const match = ua.match(/(?:OPR|Opera)\/(\d+\.\d+)/);
    version = match ? match[1] : 'Unbekannt';
  }

  return {
    name: browser,
    version: version
  };
};

/** * Ermittelt das Betriebssystem * @returns {Object} - Betriebssystem-Name und Version */ 
const getOperatingSystem = () => {
  const ua = navigator.userAgent;
  let os = 'Unbekannt';
  let version = 'Unbekannt';

  // Windows
  if (/Windows/.test(ua)) {
    os = 'Windows';
    if (/Windows NT 10.0/.test(ua)) version = '10';
    else if (/Windows NT 6.3/.test(ua)) version = '8.1';
    else if (/Windows NT 6.2/.test(ua)) version = '8';
    else if (/Windows NT 6.1/.test(ua)) version = '7';
    else if (/Windows NT 6.0/.test(ua)) version = 'Vista';
    else if (/Windows NT 5.1/.test(ua)) version = 'XP';
    else version = 'Unbekannt';
  }
  // macOS
  else if (/Mac OS X/.test(ua)) {
    os = 'macOS';
    const match = ua.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
    if (match) {
      version = match[1].replace(/_/g, '.');
    }
  }
  // iOS
  else if (/iPhone|iPad|iPod/.test(ua)) {
    os = 'iOS';
    const match = ua.match(/OS (\d+[._]\d+[._]?\d*)/);
    if (match) {
      version = match[1].replace(/_/g, '.');
    }
  }
  // Android
  else if (/Android/.test(ua)) {
    os = 'Android';
    const match = ua.match(/Android (\d+\.\d+)/);
    version = match ? match[1] : 'Unbekannt';
  }
  // Linux
  else if (/Linux/.test(ua)) {
    os = 'Linux';
  }

  return {
    name: os,
    version: version
  };
};

/** * Formatiert die gesammelten Analytics-Daten für die E-Mail * @param {Object} analytics - Gesammelte analytische Daten * @returns {string} - HTML-formatierte Daten für E-Mail */
export const formatAnalyticsForEmail = (analytics) => {
  if (!analytics) return '';
  
  return `
    <div style="margin-top: 20px; padding: 10px; border-top: 1px solid #ccc; font-family: monospace; font-size: 12px;">
      <h3 style="margin: 5px 0; color: #666;">📊 ANALYTICS-DATEN (NUR FÜR RESTAURANT)</h3>
      <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
        <tr>
          <td style="padding: 3px; color: #666; width: 140px;">Gerät:</td>
          <td style="padding: 3px;">${analytics.deviceType || 'Unbekannt'}</td>
        </tr>
        <tr>
          <td style="padding: 3px; color: #666;">Browser:</td>
          <td style="padding: 3px;">${analytics.browser?.name || 'Unbekannt'} ${analytics.browser?.version || ''}</td>
        </tr>
        <tr>
          <td style="padding: 3px; color: #666;">Betriebssystem:</td>
          <td style="padding: 3px;">${analytics.os?.name || 'Unbekannt'} ${analytics.os?.version || ''}</td>
        </tr>
        <tr>
          <td style="padding: 3px; color: #666;">Bildschirmgröße:</td>
          <td style="padding: 3px;">${analytics.screenResolution?.width || 0}x${analytics.screenResolution?.height || 0}</td>
        </tr>
        <tr>
          <td style="padding: 3px; color: #666;">Fenster:</td>
          <td style="padding: 3px;">${analytics.windowSize?.width || 0}x${analytics.windowSize?.height || 0}</td>
        </tr>
        <tr>
          <td style="padding: 3px; color: #666;">Verbindung:</td>
          <td style="padding: 3px;">${analytics.connectionType || 'Unbekannt'}</td>
        </tr>
        <tr>
          <td style="padding: 3px; color: #666;">Sprache:</td>
          <td style="padding: 3px;">${analytics.language || 'Unbekannt'}</td>
        </tr>
        <tr>
          <td style="padding: 3px; color: #666;">Zeitzone:</td>
          <td style="padding: 3px;">${analytics.timezone || 'Unbekannt'}</td>
        </tr>
        <tr>
          <td style="padding: 3px; color: #666;">IP-Adresse:</td>
          <td style="padding: 3px;">${analytics.ipAddress || 'Wird vom Server ergänzt'}</td>
        </tr>
        <tr>
          <td style="padding: 3px; color: #666;">User-Agent:</td>
          <td style="padding: 3px; font-size: 9px; word-break: break-all;">${analytics.userAgent || 'Unbekannt'}</td>
        </tr>
        <tr>
          <td style="padding: 3px; color: #666;">Quelle:</td>
          <td style="padding: 3px;">${analytics.referrer || 'Direkt'}</td>
        </tr>
      </table>
    </div>
  `;
};